import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProjectsPage.scss';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { formatLongDate } from '../../shared/helpers/date/formatLongDate';
import { Column } from 'primereact/column';
import SimpleBar from 'simplebar-react';
import { DataTable } from 'primereact/datatable';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Menu } from 'primereact/menu';
import { confirmDialog, ConfirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { TrendTag } from './preview/components/trend-tag/TrendTag';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { blendBrandScore } from '../../shared/helpers/brand-score/BrandScoreHelper';
import {
  useAssignProjectToGroup,
  useBulkAssignProjectsToGroup,
  useProjectGroups,
} from '../../shared/hooks/projects/UseProjectGroups';
import useChangeName from '../../shared/hooks/projects/UseChangeName';
import useCanAddProject from '../../shared/hooks/account/UseAddProject';
import usePermission from '../../shared/hooks/global/UsePermission';
import { isDragStartOnScrollbar } from '../../shared/helpers/dragScrollbarGuard';
import {
  duplicateProject,
  getProjectsPage,
  removeProject,
  removeProjectsBulk,
} from '../../shared/services/projects/ProjectService';
import { showCustomDialog } from '../../shared/helpers/DialogHelper';
import { ProjectResponse } from '../../shared/models/projects/ProjectResponse';
import { PROJECTS, PROJECTS_ADD } from '../../shared/routes/path';
import { canShowModel } from '../../shared/utils/utils';
import { calculateDefaultDateRange } from '../../shared/helpers/filters/DateRangeHelper';
import { useProjectsListKpis } from '../../shared/api/hooks/project/stats/useProjectsListKpis';
import { ProjectListKpi } from '../../shared/api/services/project/stats/ProjectDailyStatsService';
import ActionBtn from './components/ActionBtn';
import EducationBanner from './components/education-banner/EducationBanner';
import MoreBtn from '../../shared/components/common/more-btn/MoreBtn';
import LimitTooltipComponent from '../../shared/components/common/limit-tooltip/LimitTooltipComponent';
import Title from '../../shared/components/title/Title';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import NoResults from '../../shared/components/common/no-results/NoResults';
import Loader from '../../shared/components/common/loader/Loader';
import ModelTooltips from '../../shared/components/model-tooltips/ModelTooltips';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import VisibilityTag from '../../shared/components/visibility-tag/VisibilityTag';
import ProjectConfiguration from '../../shared/components/common/project-configuration/ProjectConfiguration';
import { getAccountOptions } from '../../shared/services/admin/users/AdminUsersService';
import CreateGroupDialog from './components/create-group-dialog/CreateGroupDialog';
import MoveToGroupMenu from './components/move-to-group-menu/MoveToGroupMenu';
import ProjectGroupSection, {
  ProjectSectionCardRenderer,
  ProjectSectionColumnBuilder,
} from './components/project-group-section/ProjectGroupSection';
import { makeSkeletonRows, withSkeletonBodies } from '../../shared/helpers/tableSkeleton';
import { useDraggableRows } from '../../shared/hooks/UseDraggableRows';
import useToast from '../../shared/hooks/global/UseToast';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../shared/utils/mobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import MobileHeaderSearch from '../../shared/components/common/mobile-menu/MobileHeaderSearch';

// Backend KPI endpoint caps at 50 project IDs. Projects beyond position 50
// will show "-" in the KPI columns. Accepted product trade-off.
const KPI_ID_CAP = 50;

// How long the renamed row keeps its lock/spinner after the save, so the reveal animation has
// room to play. Cosmetic — the new label is already applied; only the spinner lingers. Tweak freely.
const RENAME_SETTLE_MS = 700;

// Duration of the delete row collapse. Keep in sync with the `rowCollapse` keyframe in ProjectsPage.scss.
const ROW_ANIM_MS = 320;

const STAT_MODELS: { key: string; icon: string; path: string }[] = [
  { key: 'GPT', icon: 'ico--gpt-bck', path: 'gpt' },
  { key: 'GEMINI', icon: 'ico--gemini-bck', path: 'gemini' },
  { key: 'CLAUDE', icon: 'ico--claude-bck', path: 'claude' },
  { key: 'COPILOT', icon: 'ico--copilot-bck', path: 'copilot_browser' },
  { key: 'GROK', icon: 'ico--grok-bck', path: 'grok_browser' },
  { key: 'AI_OVERVIEW_NEW', icon: 'ico--ai-overview-bck', path: 'ai_overview_new_browser' },
  { key: 'AI_MODE', icon: 'ico--ai-mode', path: 'ai_mode_browser' },
  { key: 'DEEP_SEEK', icon: 'ico--deepseek-bck', path: 'deep_seek' },
  { key: 'PERPLEXITY', icon: 'ico--perplexity-bck', path: 'perplexity' },
];

const DEBOUNCE_MS = 300;

// Wrap each cell's content in a `.row-cell` div so a row can collapse/expand vertically during the
// leave/enter animations. The wrapper is `display: contents` by default (zero layout impact — the
// action column's flex, centred cells, etc. behave exactly as without it); only `--leaving` /
// `--entering` rows switch it to a collapsing block (see ProjectsPage.scss).
const withCollapsibleCells = (columns: React.ReactElement[]): React.ReactElement[] =>
  columns.map((col) => {
    const body = (col.props as { body?: (row: unknown) => React.ReactNode }).body;
    if (!body) return col;
    return React.cloneElement(col, {
      body: (row: unknown) => <div className="row-cell">{body(row)}</div>,
    } as Partial<typeof col.props>);
  });

// 0/''/'0' trend means no movement — never render a misleading up/down tag.
const hasTrend = (value: number | string): boolean => {
  const numeric = Number(value);
  if (!isNaN(numeric)) return numeric !== 0;
  return value != null && value !== '';
};

// A project whose monitors are ALL pending has no collected data yet — its KPIs come back as zeros,
// which would render as a misleading "0% / Not Visible". Show "-" for those rows instead.
const hasOnlyPendingMonitors = (project: ProjectResponse): boolean => {
  const counts = project.monitorStatusCounts;
  if (!counts) return false;
  return counts.pending > 0 && counts.active === 0 && counts.paused === 0;
};

const ProjectsPage: React.FC = () => {
  const { accountResponse, projectResponse, refetchProjectsSilently } = useAccount();
  // NEW_STATS gates the V2 preview page; on the list it makes the KPI columns mirror the V2 tile:
  // blended Brand Score (0.6×BS + 0.4×Visibility) and Average Position instead of Median.
  const hasNewStats = accountResponse?.addons?.includes('NEW_STATS') ?? false;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast, toastElement } = useToast();
  const isMobile = useIsMobile();

  // Grouped rows are HTML5-draggable (`useDraggableRows`), which disables text selection on their
  // cells — so the superadmin debug values below can't be selected/copied. Copy them on click instead.
  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for insecure contexts where the Clipboard API is unavailable.
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        showToast(t('copied'), 'success');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    },
    [showToast, t]
  );

  // Superadmin per-project configuration dialog (owner/demo overrides) — same as V1.
  const confirmDialogRef = useRef<ConfirmDialogReturn | null>(null);

  const hideDialog = useCallback(() => {
    if (!confirmDialogRef.current) return;
    confirmDialogRef.current.hide();
    // NOTE: the page queries are `['projects-page-ungrouped', ...]` / `['projects-page-group', ...]`.
    // A `['projects-page']` key does NOT match them — React Query matches by array element, not
    // string prefix — so it was a no-op. Use the actual keys.
    queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
    queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
    refetchProjectsSilently();
  }, [queryClient, refetchProjectsSilently]);

  const openConfiguration = useCallback(
    (projectId: number, demoProject: string | null, ownerAccountId: number) => {
      if (
        !projectResponse ||
        (!accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin)
      ) {
        return;
      }
      getAccountOptions()
        .then((allAccounts) => {
          confirmDialogRef.current = confirmDialog({
            message: (
              <ProjectConfiguration
                allAccounts={allAccounts}
                demoProject={demoProject}
                ownerAccountId={ownerAccountId}
                projectId={projectId}
                onSave={() => hideDialog()}
              />
            ),
            header: t('configuration'),
            footer: () => null,
          });
        })
        .catch(() => {});
    },
    [projectResponse, accountResponse, t, hideDialog]
  );

  // ── Shared pagination & sort state (ungrouped/flat section) ──────────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [first, setFirst] = useState(0);
  const [sortField, setSortField] = useState<string>('created');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Grouped mode: the ungrouped section is a collapsible accordion, expanded by default (the only one
  // expanded — named group sections default to collapsed).
  const [ungroupedCollapsed, setUngroupedCollapsed] = useState(false);

  // Search — immediate input text + debounced query driver
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [loader, setLoader] = useState(false);

  // Per-row mutation state — the affected row(s) lock + show a centred spinner instead of the
  // full-page `<Loader />` flash (see `.project-row--mutating` in ProjectsPage.scss).
  const [mutatingIds, setMutatingIds] = useState<Set<number>>(new Set());
  const startRowMutation = useCallback((ids: number | number[]) => {
    const list = Array.isArray(ids) ? ids : [ids];
    setMutatingIds((prev) => {
      const next = new Set(prev);
      list.forEach((id) => next.add(id));
      return next;
    });
  }, []);
  const endRowMutation = useCallback((ids: number | number[]) => {
    const list = Array.isArray(ids) ? ids : [ids];
    setMutatingIds((prev) => {
      const next = new Set(prev);
      list.forEach((id) => next.delete(id));
      return next;
    });
  }, []);
  // Rows animating out (delete). Separate from `mutatingIds` (the in-flight spinner) so the collapse
  // keyframe plays after the request settles.
  const [leavingIds, setLeavingIds] = useState<Set<number>>(new Set());

  const rowClassName = useCallback(
    (rowData: ProjectResponse) => {
      const id = rowData.id;
      if (id == null) return '';
      if (leavingIds.has(id)) return 'project-row--leaving';
      if (mutatingIds.has(id)) return 'project-row--mutating';
      return '';
    },
    [mutatingIds, leavingIds]
  );

  // Selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<ProjectResponse[]>([]);

  // Create group dialog
  const [createGroupVisible, setCreateGroupVisible] = useState(false);

  // Move-to-group per-row popup
  const moveMenuRefs = useRef<Map<number, Menu>>(new Map());
  const [openMoveMenuId, setOpenMoveMenuId] = useState<number | null>(null);

  // Drag & drop state
  const [dragOverGroupId, setDragOverGroupId] = useState<number | null>(null);
  const [dragOverUngrouped, setDragOverUngrouped] = useState(false);
  const [draggedProjectId, setDraggedProjectId] = useState<number | null>(null);

  const { data: groups = [], isPending: groupsPending } = useProjectGroups();
  const { mutate: assignToGroup } = useAssignProjectToGroup();
  const { mutate: bulkAssignToGroup } = useBulkAssignProjectsToGroup();
  const { canAddProject, isBillingLimit } = useCanAddProject();
  const projectsAccess = usePermission('PROJECTS');

  // showGrouped: when the account has ≥1 project group, use multi-section grouped mode.
  // Flat server-paginated mode is used only when no groups exist.
  const showGrouped = groups.length > 0;

  // Make the ungrouped section's rows draggable (group rows are handled inside ProjectGroupSection), so
  // a project can be dragged between the ungrouped section and a group.
  const ungroupedBodyRef = useRef<HTMLDivElement>(null);
  useDraggableRows(ungroupedBodyRef, showGrouped && !isMobile);

  const sortedGroups = useMemo(() => [...groups].sort((a, b) => a.position - b.position), [groups]);

  // ── Scroll-to-close for move-to-group popup ───────────────────────────────
  useEffect(() => {
    if (!openMoveMenuId) return;
    const handleScroll = () => {
      const ref = moveMenuRefs.current.get(openMoveMenuId);
      ref?.hide({} as React.SyntheticEvent);
      setOpenMoveMenuId(null);
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [openMoveMenuId]);

  // ── Debounce search input ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
      setFirst(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // ── Ungrouped section: server-side query ──────────────────────────────────
  // When groups exist: fetch only ungrouped projects (ungrouped: true).
  // When no groups exist: fetch all projects (flat mode, no group filter).
  // `showGrouped` MUST be part of the key: it flips from false to true once the groups query resolves
  // and it changes the request (all projects → ungrouped only). Without it the first, unfiltered
  // response stays cached under the same key, so a grouped project renders in BOTH sections.
  const ungroupedQueryKey = useMemo(
    () => [
      'projects-page-ungrouped',
      showGrouped,
      page,
      pageSize,
      debouncedSearch,
      sortField,
      sortOrder,
    ],
    [showGrouped, page, pageSize, debouncedSearch, sortField, sortOrder]
  );

  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: ungroupedQueryKey,
    queryFn: () =>
      getProjectsPage({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sort: sortField,
        order: sortOrder,
        ungrouped: showGrouped ? true : undefined,
      }),
    // Wait for the groups query — until it lands we don't know whether to ask for ungrouped-only, and
    // firing early would both waste a request and flash grouped projects in this section.
    enabled: !groupsPending,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const flatItems: ProjectResponse[] = data?.items ?? [];
  const total: number = data?.total ?? 0;

  // Skeleton on navigation (page/sort) and first load, never on mutation refetches. `isPlaceholderData`
  // is true while showing a previous key's data during a page/sort change; `!data` covers the initial
  // fetch (no cache yet). A mutation invalidates the SAME key → `isPlaceholderData` false AND `data`
  // present → no skeleton (the row animates instead).
  // Always show the skeleton while a navigation / first-load fetch is in flight (no delay, no enter
  // animation — kept deliberately simple). A mutation refetches the SAME key (`isPlaceholderData` false,
  // `data` present), so it never skeletons — its row animates (`--mutating` / `--leaving`) instead.
  // `groupsPending` counts as loading too — the projects query is gated on it, so without this the
  // section would flash its empty state before the first fetch even starts.
  const showSkeleton = groupsPending || (isFetching && (isPlaceholderData || !data));

  // ── KPI columns for ungrouped / flat section ──────────────────────────────
  // Use the SAME default window as the preview pages (LAST_30_DAYS = subDays(today, 29)..today) so the
  // list KPIs match the tile on ProjectsPreviewPage(V2). `getDefaultRange()` (subMonths(today, 1)) was
  // ~2 days wider, shifting Brand Score / position / visibility by up to several %. The preview clamps
  // the lower bound to each project's oldest data date, but that only narrows the displayed bounds — it
  // never changes the aggregate (no rows exist before a project's oldest date), so the unclamped
  // 30-day window (epoch passed as oldestDate) yields identical numbers per project.
  const kpiDateRange = useMemo(() => calculateDefaultDateRange(new Date(0)).range, []);
  const kpiFrom = kpiDateRange[0];
  const kpiTo = kpiDateRange[1];

  const pageProjectIds = useMemo(
    () =>
      flatItems
        .map((p) => p.id)
        .filter((id): id is number => id != null)
        .slice(0, KPI_ID_CAP),
    [flatItems]
  );

  const { kpis, isLoading: kpisLoading } = useProjectsListKpis(pageProjectIds, kpiFrom, kpiTo);

  // ── Mutation helpers ──────────────────────────────────────────────────────
  const refreshProjects = useCallback(() => {
    setLoader(false);
    refetchProjectsSilently();
  }, [refetchProjectsSilently]);

  const { editName } = useChangeName({
    onRefresh: refreshProjects,
    onSaveStart: startRowMutation,
    onSaveEnd: endRowMutation,
    settleDelayMs: RENAME_SETTLE_MS,
  });

  // Drop rows from the cached `projects-page-*` pages in place (optimistic), so they vanish right after
  // their leave animation without waiting for a refetch. Total is corrected by the quiet refill below.
  const dropRowsFromPageCache = useCallback(
    (ids: number[]) => {
      const idSet = new Set(ids);
      const drop = (old: { items?: ProjectResponse[] } | undefined) =>
        old?.items ? { ...old, items: old.items.filter((p) => !idSet.has(p.id)) } : old;
      queryClient.setQueriesData({ queryKey: ['projects-page-ungrouped'] }, drop);
      queryClient.setQueriesData({ queryKey: ['projects-page-group'] }, drop);
    },
    [queryClient]
  );

  // Play the leave animation, then remove the rows + quietly refill so total/pagination stay exact.
  // The refill refetches the SAME key → `isPlaceholderData` false → no skeleton; `loading` is removed
  // → no mask. So the deletion reads as a clean fade-out with no flash.
  const animateRemoval = useCallback(
    async (ids: number[]) => {
      setLeavingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      await new Promise((resolve) => setTimeout(resolve, ROW_ANIM_MS));
      dropRowsFromPageCache(ids);
      setLeavingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
      queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
      refetchProjectsSilently();
    },
    [dropRowsFromPageCache, queryClient, refetchProjectsSilently]
  );

  // Single delete is implemented directly (not via `useRemoveProject`) so it stays NON-destructive
  // (`useRemoveProject`'s `refetchData()` nulls the context and unmounts the list). Flow: row spinner
  // while the request is in flight → leave animation → optimistic remove + quiet refill.
  const confirmRemove = useCallback(
    (id: number, name?: string) => {
      showCustomDialog({
        ...(name && { message: t('removeProjectConfirm', { name }) }),
        accept: async () => {
          startRowMutation(id);
          try {
            await removeProject(id);
            endRowMutation(id);
            await animateRemoval([id]);
          } catch (error) {
            console.error('Error removing project:', error);
            endRowMutation(id);
          }
        },
      });
    },
    [startRowMutation, endRowMutation, animateRemoval, t]
  );

  const confirmDuplicate = (id: number) => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => {
        if (!canAddProject) return;
        startRowMutation(id);
        duplicateProject(id)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
            queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
            refetchProjectsSilently();
          })
          .finally(() => endRowMutation(id));
      },
    });
  };

  const confirmBulkRemove = () => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: async () => {
        const ids = selectedProjects.map((p) => p.id).filter((x): x is number => x != null);
        startRowMutation(ids);
        try {
          await removeProjectsBulk(ids);
          setSelectedProjects([]);
          endRowMutation(ids);
          await animateRemoval(ids);
        } catch {
          endRowMutation(ids);
        }
      },
    });
  };

  // After any assign mutation, invalidate all section queries and group list.
  const invalidateAfterAssign = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
    queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
    queryClient.invalidateQueries({ queryKey: ['project-groups'] });
  }, [queryClient]);

  // ── Flat-mode pagination/sort ─────────────────────────────────────────────
  const handleFlatPaginatorChange = (e: PaginatorPageChangeEvent) => {
    setPage(e.page);
    setFirst(e.first);
    setPageSize(e.rows);
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const handleRowDragStart = useCallback((e: React.DragEvent) => {
    // Grabbing a SimpleBar scrollbar (the horizontal track overlaps the last row) must scroll, not
    // start a row drag. Cancel the native drag when it began on the scrollbar.
    if (isDragStartOnScrollbar(e)) {
      e.preventDefault();
      return;
    }
    const tr = (e.target as HTMLElement).closest('tr');
    if (!tr) return;
    const marker = tr.querySelector<HTMLElement>('[data-project-id]');
    if (!marker) return;
    const projectId = marker.dataset.projectId;
    if (!projectId) return;
    e.dataTransfer.setData('application/project-id', projectId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedProjectId(Number(projectId));

    const ghost = document.createElement('div');
    ghost.className = 'project-drag-ghost';
    ghost.textContent = `Project #${projectId}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  }, []);

  const handleProjectDragEnd = useCallback(() => {
    setDragOverGroupId(null);
    setDragOverUngrouped(false);
    setDraggedProjectId(null);
  }, []);

  const handleGroupDragOver = useCallback(
    (e: React.DragEvent, groupId: number) => {
      if (!e.dataTransfer.types.includes('application/project-id')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverGroupId !== groupId) setDragOverGroupId(groupId);
    },
    [dragOverGroupId]
  );

  const handleGroupDrop = useCallback(
    (e: React.DragEvent, groupId: number) => {
      setDragOverGroupId(null);
      const projectIdStr = e.dataTransfer.getData('application/project-id');
      if (!projectIdStr) return;
      e.preventDefault();
      assignToGroup({ projectId: Number(projectIdStr), groupId }, { onSuccess: invalidateAfterAssign });
    },
    [assignToGroup, invalidateAfterAssign]
  );

  const handleGroupDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear when the cursor leaves the group section entirely.
    const related = e.relatedTarget as HTMLElement | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDragOverGroupId(null);
  }, []);

  // Drop onto the ungrouped section → remove the project from its group (assign `groupId: null`).
  const handleUngroupedDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('application/project-id')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverUngrouped(true);
  }, []);

  const handleUngroupedDrop = useCallback(
    (e: React.DragEvent) => {
      setDragOverUngrouped(false);
      const projectIdStr = e.dataTransfer.getData('application/project-id');
      if (!projectIdStr) return;
      e.preventDefault();
      assignToGroup(
        { projectId: Number(projectIdStr), groupId: null },
        { onSuccess: invalidateAfterAssign }
      );
    },
    [assignToGroup, invalidateAfterAssign]
  );

  const handleUngroupedDragLeave = useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as HTMLElement | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDragOverUngrouped(false);
  }, []);

  // ── Body templates ────────────────────────────────────────────────────────
  const projectNameBodyTemplate = useCallback(
    (project: ProjectResponse) => {
      if (!accountResponse) return null;
      const period = project.defaultPeriod.toLowerCase();
      return (
        <div className="project-name" data-project-id={project.id}>
          <span className="ico--folder ico--sm" />
          <NavLink to={`${PROJECTS}/${project.id}/${period}/all`}>
            {project.projectLabel} {project.demoVersion && '(Demo)'}
          </NavLink>
          {accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin && (
            <div className="project-name__additional">
              isSuperAdminProject:
              {project.accountId === 1 ? (
                <span className="project-name__ok">true</span>
              ) : (
                <span>false</span>
              )}
              | accountId:{' '}
              <span
                className="project-name__copyable"
                title={t('copy')}
                onClick={() => copyToClipboard(String(project.accountId))}
              >
                {project.accountId}
              </span>{' '}
              | email:{' '}
              {project.additionalInfo?.ownerEmail ? (
                <a
                  href={`mailto:${project.additionalInfo.ownerEmail}`}
                  className="link project-name__copyable"
                  title={t('copy')}
                  onClick={(e) => {
                    e.preventDefault();
                    copyToClipboard(project.additionalInfo!.ownerEmail);
                  }}
                >
                  {project.additionalInfo.ownerEmail}
                </a>
              ) : (
                <span>-</span>
              )}{' '}
              | domain:{' '}
              {project.domain ? (
                <a
                  href={`http://${project.domain}`}
                  className="link project-name__copyable"
                  title={t('copy')}
                  onClick={(e) => {
                    e.preventDefault();
                    copyToClipboard(project.domain);
                  }}
                >
                  {project.domain}
                </a>
              ) : (
                <span>-</span>
              )}{' '}
              | brandName:{' '}
              {project.firmName ? (
                <span
                  className="project-name__copyable"
                  title={t('copy')}
                  onClick={() => copyToClipboard(project.firmName)}
                >
                  {project.firmName}
                </span>
              ) : (
                <span>-</span>
              )}{' '}
              |
              <Button
                className="btn--link btn--link-blue"
                label="configuration"
                onClick={() => openConfiguration(project.id, project.demoVersion, project.accountId)}
              />
            </div>
          )}
        </div>
      );
    },
    [accountResponse, openConfiguration, copyToClipboard, t]
  );

  const statsBodyTemplate = useCallback(
    (project: ProjectResponse) => {
      if (!accountResponse) return null;
      const period = project.defaultPeriod.toLowerCase();
      // Superadmin viewing client projects: show the project OWNER's models (ownerFeatures); for own
      // projects ownerFeatures is absent, so fall back to the logged-in account's features.
      const features = project.ownerFeatures ?? accountResponse.features;
      const visibleModels = STAT_MODELS.filter((model) => canShowModel(model.key, features));
      return (
        <div className="model-stack">
          {visibleModels.map((model) => (
            <span
              key={model.key}
              className="model-stack__item"
              onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/${model.path}`)}
            >
              <span className={`${model.icon} ico--md`} />
            </span>
          ))}
        </div>
      );
    },
    [accountResponse, navigate]
  );

  const promptCountBodyTemplate = useCallback(
    (statusKey: 'active' | 'pending' | 'paused') => (project: ProjectResponse) => {
      const value = project.monitorStatusCounts?.[statusKey] ?? 0;
      return <span className="prompt-count">{value}</span>;
    },
    []
  );

  // KPI body templates are parameterized to support both page-level kpis (ungrouped section)
  // and per-section kpis (group sections). The buildColumns factory accepts kpis + kpisLoading.
  const makeBrandScoreBody =
    (kpisMap: Record<number, ProjectListKpi>, loading: boolean) => (project: ProjectResponse) => {
      if (loading) return <span className="skeleton--cell" />;
      const kpi = project.id != null ? kpisMap[project.id] : undefined;
      if (!kpi || hasOnlyPendingMonitors(project)) return <span>-</span>;
      // NEW_STATS: same value as the V2 tile — blend the raw brand score with this row's visibility.
      const brandScoreValue = hasNewStats
        ? Number(blendBrandScore(kpi.brandScore, kpi.visibilityPercent))
        : kpi.brandScore;
      return (
        <span className="kpi-cell">
          {Math.round(brandScoreValue)}%
          {hasTrend(kpi.brandScoreTrend) && (
            <TrendTag
              trendValue={kpi.brandScoreTrend}
              suffix="%"
              size="SM"
              tooltipId={`bs-${project.id}`}
            />
          )}
        </span>
      );
    };

  const makeMedianPositionBody =
    (kpisMap: Record<number, ProjectListKpi>, loading: boolean) => (project: ProjectResponse) => {
      if (loading) return <span className="skeleton--cell" />;
      const kpi = project.id != null ? kpisMap[project.id] : undefined;
      if (!kpi) return <span>-</span>;
      // NEW_STATS: show Average Position (matches the V2 tile) instead of Median.
      const positionValue = hasNewStats ? kpi.averagePosition : kpi.medianPosition;
      const positionTrend = hasNewStats ? kpi.averagePositionTrend : kpi.medianPositionTrend;
      return (
        <span className="kpi-cell">
          {positionValue > 0 ? positionValue.toFixed(1) : '-'}
          {positionValue > 0 && hasTrend(positionTrend) && (
            <TrendTag
              trendValue={positionTrend}
              suffix=""
              size="SM"
              tooltipId={`mp-${project.id}`}
            />
          )}
        </span>
      );
    };

  const makeVisibilityBody =
    (kpisMap: Record<number, ProjectListKpi>, loading: boolean) => (project: ProjectResponse) => {
      if (loading) return <span className="skeleton--cell skeleton--cell-max" />;
      const kpi = project.id != null ? kpisMap[project.id] : undefined;
      if (!kpi || hasOnlyPendingMonitors(project)) return <span>-</span>;
      // NEW_STATS: base the level on the actual Visibility metric (like the V2 tile); otherwise derive
      // it from Brand Score (V1 tile behavior).
      return <VisibilityTag percent={hasNewStats ? kpi.visibilityPercent : kpi.brandScore} />;
    };

  const renderActionColumn = useCallback(
    (project: ProjectResponse) => {
      if (!accountResponse) return null;
      // Całe menu wiersza to same zmiany: usunięcie, duplikat, zmiana nazwy, przeniesienie do grupy.
      if (!projectsAccess.write) return null;

      const isOwnProject = project.accountId === accountResponse.accountId;
      const canMoveToGroup = isOwnProject && !project.demoVersion && groups.length > 0;

      const handleAssign = (targetGroupId: number | null) => {
        const ref = moveMenuRefs.current.get(project.id);
        ref?.hide({} as React.SyntheticEvent);
        assignToGroup(
          { projectId: project.id, groupId: targetGroupId },
          { onSuccess: invalidateAfterAssign }
        );
      };

      const availableGroupsForRow = canMoveToGroup
        ? groups.filter((g) => g.id !== project.projectGroupId)
        : [];

      const moveMenuModel = [
        ...(project.projectGroupId !== null && project.projectGroupId !== undefined
          ? [
              {
                template: () => (
                  <div className="move-to-group-menu__item" onClick={() => handleAssign(null)}>
                    <span>{t('ungrouped')}</span>
                  </div>
                ),
              },
              ...(availableGroupsForRow.length > 0 ? [{ separator: true }] : []),
            ]
          : []),
        ...availableGroupsForRow.map((group) => ({
          template: () => (
            <div className="move-to-group-menu__item" onClick={() => handleAssign(group.id)}>
              <span
                className="move-to-group-menu__dot"
                style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
              />
              <span>{group.name}</span>
            </div>
          ),
        })),
      ];

      return (
        <div className="table__cell--actions">
          <MoreBtn rowId={project.id || 0}>
            <ActionBtn
              confirmDuplicate={confirmDuplicate}
              projectId={project.id}
              confirmRemove={(id) => confirmRemove(id, project.projectLabel)}
              accountStatus={accountResponse.accountStatus}
              demoVersion={project.demoVersion}
              editName={editName}
              isSuperAdminPermission={
                accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin
              }
              canMoveToGroup={canMoveToGroup}
              onMoveClick={(e) => {
                const ref = moveMenuRefs.current.get(project.id);
                ref?.toggle(e);
                setOpenMoveMenuId(project.id);
              }}
            />
          </MoreBtn>
          {canMoveToGroup && (
            <Menu
              ref={(el) => {
                if (el) moveMenuRefs.current.set(project.id, el);
                else moveMenuRefs.current.delete(project.id);
              }}
              popup
              appendTo={document.body}
              className="move-to-group-menu__popup"
              onHide={() => setOpenMoveMenuId(null)}
              model={moveMenuModel}
            />
          )}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accountResponse,
      confirmRemove,
      editName,
      groups,
      assignToGroup,
      invalidateAfterAssign,
      projectsAccess.write,
      t,
    ]
  );

  // ── Column builders ───────────────────────────────────────────────────────
  // buildColumns is used for BOTH the ungrouped/flat section and each group section.
  // It is parameterized by kpis + kpisLoading so each section supplies its own KPI data.
  const buildColumns: ProjectSectionColumnBuilder = useCallback(
    (tableItems: ProjectResponse[], kpisMap: Record<number, ProjectListKpi>, loading: boolean) => {
      const cols = [
        <Column
          key="projectLabel"
          sortable
          field="projectLabel"
          header={t('projectName')}
          body={projectNameBodyTemplate}
          headerClassName="table__header"
          style={{ minWidth: '180px', maxWidth: '300px' }}
        />,
        <Column
          key="stats"
          exportable={false}
          header={t('stats')}
          body={statsBodyTemplate}
          headerClassName="table__header"
          style={{ minWidth: '290px', maxWidth: '300px' }}
        />,
        <Column
          key="brandScore"
          exportable={false}
          header={t('brandScore')}
          body={makeBrandScoreBody(kpisMap, loading)}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '70px', maxWidth: '80px' }}
        />,
        <Column
          key="medianPosition"
          exportable={false}
          header={hasNewStats ? t('averagePosition') : t('medianPosition')}
          body={makeMedianPositionBody(kpisMap, loading)}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '80px', maxWidth: '90px' }}
        />,
        <Column
          key="visibility"
          exportable={false}
          header={t('visibility')}
          body={makeVisibilityBody(kpisMap, loading)}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '140px', maxWidth: '150px' }}
        />,
        <Column
          key="promptsActive"
          exportable={false}
          header={t('active')}
          body={promptCountBodyTemplate('active')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '80px', maxWidth: '90px' }}
        />,
        <Column
          key="promptsPending"
          exportable={false}
          header={t('pending')}
          body={promptCountBodyTemplate('pending')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '80px', maxWidth: '90px' }}
        />,
        <Column
          key="promptsPaused"
          exportable={false}
          header={t('paused')}
          body={promptCountBodyTemplate('paused')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          style={{ minWidth: '80px', maxWidth: '90px' }}
        />,
        <Column
          key="created"
          sortable
          field="created"
          header={t('created')}
          body={(data: ProjectResponse) => formatLongDate(data.created, i18n.language)}
          headerClassName="table__header"
          bodyClassName="table__cell--nowrap"
          style={{ width: '145px' }}
        />,
      ];

      // Bez prawa zapisu `renderActionColumn` i tak zwraca `null` w każdym wierszu — zostawałby pusty,
      // zamrożony słupek przy prawej krawędzi tabeli.
      if (projectsAccess.write) {
        cols.push(
          <Column
            key="action"
            exportable={false}
            frozen
            alignFrozen="right"
            header={t('action')}
            body={renderActionColumn}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--gap"
            align="center"
            style={{ width: '34px' }}
          />
        );
      }

      if (selectionMode) {
        cols.unshift(
          <Column
            key="checkbox"
            exportable={false}
            headerStyle={{ width: '20px', padding: '10px 0 10px 25px' }}
            bodyStyle={{ padding: '10px 0 10px 25px' }}
            header={
              <Checkbox
                checked={
                  tableItems.length > 0 &&
                  tableItems.every((p) => selectedProjects.some((sp) => sp.id === p.id))
                }
                onChange={(e) => {
                  if (e.checked) {
                    setSelectedProjects((prev) => {
                      const ids = new Set(prev.map((p) => p.id));
                      return [...prev, ...tableItems.filter((p) => !ids.has(p.id))];
                    });
                  } else {
                    const tableIds = new Set(tableItems.map((p) => p.id));
                    setSelectedProjects((prev) => prev.filter((p) => !tableIds.has(p.id)));
                  }
                }}
              />
            }
            body={(project: ProjectResponse) => (
              <Checkbox
                checked={selectedProjects.some((p) => p.id === project.id)}
                onChange={(e) => {
                  if (e.checked) {
                    setSelectedProjects((prev) => [...prev, project]);
                  } else {
                    setSelectedProjects((prev) => prev.filter((p) => p.id !== project.id));
                  }
                }}
              />
            )}
          />
        );
      }

      // Wrap real bodies in `.row-cell` (for collapse/expand animations), then in the skeleton wrapper
      // (placeholder rows short-circuit to a shimmer bar; both reuse THESE columns — no duplicate markup).
      return withSkeletonBodies(withCollapsibleCells(cols));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      hasNewStats,
      projectNameBodyTemplate,
      statsBodyTemplate,
      promptCountBodyTemplate,
      renderActionColumn,
      projectsAccess.write,
      selectionMode,
      selectedProjects,
    ]
  );

  // ── Mobile cards ──────────────────────────────────────────────────────────
  // Telefon: zamiast wierszy tabeli karty z TYMI SAMYMI komórkami (nazwa, modele, KPI, liczniki, menu
  // akcji), więc liczby i akcje nie mają osobnej, mobilnej wersji. Sortowania i przeciągania do grup
  // na kartach nie ma — do grupy przenosi pozycja w menu akcji.
  const renderCards: ProjectSectionCardRenderer = useCallback(
    (tableItems: ProjectResponse[], kpisMap: Record<number, ProjectListKpi>, loading: boolean, skeleton: boolean) => {
      if (skeleton) {
        return (
          <div className="project-cards">
            {[0, 1, 2].map((i) => (
              <div key={i} className="project-card project-card--skeleton">
                <span className="skeleton--cell" />
                <span className="skeleton--cell skeleton--cell-max" />
                <span className="skeleton--cell" />
              </div>
            ))}
          </div>
        );
      }

      const kpiColumns = [
        { key: 'brandScore', label: t('brandScore'), body: makeBrandScoreBody(kpisMap, loading) },
        {
          key: 'position',
          label: hasNewStats ? t('averagePosition') : t('medianPosition'),
          body: makeMedianPositionBody(kpisMap, loading),
        },
        { key: 'visibility', label: t('visibility'), body: makeVisibilityBody(kpisMap, loading) },
      ];

      return (
        <div className="project-cards">
          {tableItems.map((project) => {
            const stateClass = leavingIds.has(project.id)
              ? ' project-card--leaving'
              : mutatingIds.has(project.id)
                ? ' project-card--mutating'
                : '';
            return (
              <div key={project.id} className={`project-card${stateClass}`}>
                <div className="project-card__header">
                  {selectionMode && (
                    <Checkbox
                      checked={selectedProjects.some((p) => p.id === project.id)}
                      onChange={(e) =>
                        setSelectedProjects((prev) =>
                          e.checked ? [...prev, project] : prev.filter((p) => p.id !== project.id)
                        )
                      }
                    />
                  )}
                  {projectNameBodyTemplate(project)}
                  {renderActionColumn(project)}
                </div>
                {statsBodyTemplate(project)}
                <div className="project-card__kpis">
                  {kpiColumns.map((kpi) => (
                    <div key={kpi.key} className="project-card__kpi">
                      <span className="project-card__label">{kpi.label}</span>
                      <span className="project-card__value">{kpi.body(project)}</span>
                    </div>
                  ))}
                </div>
                <div className="project-card__counts">
                  {(['active', 'pending', 'paused'] as const).map((key) => (
                    <span key={key} className="project-card__count">
                      <span className="project-card__label">{t(key)}</span>
                      {project.monitorStatusCounts?.[key] ?? 0}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      hasNewStats,
      projectNameBodyTemplate,
      statsBodyTemplate,
      renderActionColumn,
      selectionMode,
      selectedProjects,
      mutatingIds,
      leavingIds,
    ]
  );

  // ── Add project button ────────────────────────────────────────────────────
  const getAddProjectButton = useCallback(() => {
    if (!accountResponse) return null;
    if (['BLOCKED'].includes(accountResponse.accountStatus)) return <></>;
    // Bez prawa zapisu (dziś: gość) przycisk znika zamiast straszyć tooltipem o limicie — limit nie
    // jest tu problemem, brak uprawnienia jest.
    if (!projectsAccess.write) return <></>;

    const tooltipText = isBillingLimit ? 'reachedLimitProject' : undefined;

    return (
      <>
        <Button
          className={`btn btn--sm ${!canAddProject ? 'btn--disabled-tooltip-color' : ''}`}
          onClick={() => {
            if (canAddProject) navigate(PROJECTS_ADD);
          }}
          label={t('addNewProject')}
          id="add-prompt-v2"
          icon={<span className="ico--add ico--xs" />}
        />
        {!canAddProject && (
          <LimitTooltipComponent tooltipText={tooltipText} id="add-prompt-v2" direction="bottom" />
        )}
      </>
    );
  }, [accountResponse, canAddProject, isBillingLimit, navigate, projectsAccess.write, t]);

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setSelectedProjects([]);
      return !prev;
    });
  };

  // ── Toolbar ───────────────────────────────────────────────────────────────
  // Akcje masowe dla zaznaczonych — w pasku narzędzi, a na telefonie osobno pod górnym paskiem.
  const bulkPanel = selectionMode && selectedProjects.length > 0 && (
    <div className="page-box__buttons-panel">
      {groups.length > 0 && selectedProjects.some((p) => !p.demoVersion) && (
        <MoveToGroupMenu
          groups={groups}
          currentGroupId={null}
          onSelect={(targetGroupId) => {
            bulkAssignToGroup(
              {
                projectIds: selectedProjects.filter((p) => !p.demoVersion).map((p) => p.id),
                groupId: targetGroupId,
              },
              { onSuccess: invalidateAfterAssign }
            );
            setSelectedProjects([]);
          }}
          buttonId="bulk-move-to-group-btn-v2"
        />
      )}
      <Button
        className="action-btn action-btn--danger"
        onClick={confirmBulkRemove}
        id="bulk-remove-projects-btn"
      >
        <span className="ico--trash ico--xs" />
      </Button>
      <CustomTooltip tooltipText="bulkRemove" targetRef="bulk-remove-projects-btn" />
    </div>
  );

  const toolbar = (
    <div className="page-box__buttons">
      {bulkPanel}
      {!isMobile && (
        <div className="input-search-box">
          <InputText
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search..."
            className="input-search"
          />
        </div>
      )}
      {!isMobile && getAddProjectButton()}
      {!isMobile && projectsAccess.write && (
        <>
          <Button
            className="btn btn--sm btn--secondary"
            onClick={() => setCreateGroupVisible(true)}
            label={t('createGroup')}
            id="create-group-btn-v2"
            icon={<span className="ico--add ico--xs" />}
          />
          {/* Tryb zaznaczania służy wyłącznie akcjom masowym (usuń, przenieś do grupy). */}
          <Button
            className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
            onClick={toggleSelectionMode}
            id="select-mode-projects-v2-btn"
          >
            <span className="ico--select ico--xs" />
          </Button>
          <CustomTooltip tooltipText="selectModeProjects" targetRef="select-mode-projects-v2-btn" />
        </>
      )}
    </div>
  );

  // Telefon: wyszukiwarka, tworzenie grupy, tryb zaznaczania i „nowy projekt” w górnym pasku
  // (`MobilePageHeader`). „+” jest tylko tutaj — na liście projektów, nie w pasku każdej strony.
  const mobileHeaderActions = (
    <>
      <MobileHeaderSearch value={searchText} onChange={setSearchText} />
      {projectsAccess.write && (
        <>
          <button
            type="button"
            className="mobile-header__icon-btn"
            onClick={() => setCreateGroupVisible(true)}
            aria-label={t('createGroup')}
          >
            <span className="ico--folder ico--sm" />
            <span className="mobile-header__icon-badge" />
          </button>
          <button
            type="button"
            className={`mobile-header__icon-btn${selectionMode ? ' mobile-header__icon-btn--active' : ''}`}
            onClick={toggleSelectionMode}
            aria-label={t('selectModeProjects')}
          >
            <span className="ico--select ico--sm" />
          </button>
          {canAddProject && (
            <button
              type="button"
              className="btn btn--sm mobile-header__add"
              onClick={() => navigate(PROJECTS_ADD)}
              aria-label={t('addNewProject')}
            >
              <span className="ico--add ico--xs" />
            </button>
          )}
        </>
      )}
    </>
  );

  // ── Empty / loader states ─────────────────────────────────────────────────
  if (!accountResponse || loader || canAddProject === null) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  // `!isPlaceholderData` keeps the "no projects" empty state from flashing mid-navigation (a page change
  // briefly holds the previous page's data as placeholder before the new data settles).
  // `!groupsPending` covers the window before the projects query is even allowed to start: it is gated
  // on the groups query, so until that lands `isFetching` is false and `total` is 0 — which reads as
  // "no projects" and flashes the empty state (`showSkeleton` guards the same window for the table).
  if (
    !groupsPending &&
    !showGrouped &&
    !isFetching &&
    !isPlaceholderData &&
    total === 0 &&
    !debouncedSearch
  ) {
    const emptyStateData = {
      title: t('emptyStateProject'),
      imageClassName: 'img--project',
      children: getAddProjectButton(),
    };
    return (
      <div className="project-page page-content">
        <div className="empty-state-page">
          <EmptyState emptyStateData={emptyStateData} />
        </div>
      </div>
    );
  }

  const emptyFlatTemplate = () => <NoResults />;

  // Searching with no matches → the shared "No results found" state; a genuinely empty
  // ungrouped section keeps the compact text.
  const emptyUngroupedTemplate = () =>
    debouncedSearch ? (
      <NoResults />
    ) : (
      <NoResults icon="ico--folder" titleKey="emptyProjectGroup" descKey="" />
    );

  const dtSortOrder = sortOrder === 'ASC' ? 1 : -1;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="project-page page-content"
      onDragStart={showGrouped ? handleRowDragStart : undefined}
      onDragEnd={showGrouped ? handleProjectDragEnd : undefined}
    >
      {toastElement}
      <MobilePageHeader title={<span>{t('allProjects')}</span>} actions={mobileHeaderActions} />
      <ConfirmDialog className="dialog-box" />
      <CreateGroupDialog
        visible={createGroupVisible}
        onHide={() => {
          setCreateGroupVisible(false);
          queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
          queryClient.invalidateQueries({ queryKey: ['project-groups'] });
        }}
      />
      <div className="page-box" key="page-box-project-v2">
        {isMobile ? (
          bulkPanel && <div className="project-page__mobile-bulk">{bulkPanel}</div>
        ) : (
          <div className="page-box__filters" data-observe-sticky>
            <Title title={t('allProjects')} children={toolbar} btnLabel={t('addNewProject')} />
          </div>
        )}

        <EducationBanner />

        {showGrouped ? (
          // ── Grouped mode: one card per group (collapsed by default) + ungrouped section ──
          // Wrapped in .table-content so the cards get the same 56px side padding as V1
          // (and the flat table) instead of spanning the full page width.
          <div className="table-content">
            <div className="projects-sections">
              {sortedGroups.map((group) => (
                <ProjectGroupSection
                  key={group.id}
                  group={group}
                  buildColumns={buildColumns}
                  renderCards={renderCards}
                  kpiFrom={kpiFrom}
                  kpiTo={kpiTo}
                  debouncedSearch={debouncedSearch}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onAssignSuccess={invalidateAfterAssign}
                  dragOverGroupId={dragOverGroupId}
                  draggedProjectId={draggedProjectId}
                  onDragOver={handleGroupDragOver}
                  onDrop={handleGroupDrop}
                  onDragLeave={handleGroupDragLeave}
                  rowClassName={rowClassName}
                />
              ))}

              {/* Ungrouped section — collapsible accordion, expanded by default (the only expanded one).
                  Reuses the group-section card + header classes for visual consistency; the table,
                  pagination and query logic are unchanged from the previous inline section. Also a drop
                  zone: dropping a project here removes it from its group (assign `groupId: null`). */}
              <div
                className={`project-group-section${
                  dragOverUngrouped ? ' project-group-section--drag-over' : ''
                }`}
                onDragOver={handleUngroupedDragOver}
                onDrop={handleUngroupedDrop}
                onDragLeave={handleUngroupedDragLeave}
              >
                <div
                  className={`project-group-header project-group-header--ungrouped${
                    ungroupedCollapsed ? ' project-group-header--collapsed' : ''
                  }`}
                  onClick={() => setUngroupedCollapsed((c) => !c)}
                >
                  <div className="project-group-header__left">
                    <span className="ico--up ico--xs project-group-header__chevron" />
                    <span
                      className="project-group-header__dot"
                      style={{ backgroundColor: 'var(--grey-ux-300)' }}
                    />
                    <span className="project-group-header__name">{t('ungrouped')}</span>
                    <span className="project-group-header__count">{total}</span>
                  </div>
                </div>
                <div
                  className={`project-group-section__reveal${
                    !ungroupedCollapsed ? ' project-group-section__reveal--expanded' : ''
                  }`}
                >
                  <div className="project-group-section__reveal-inner">
                    <div className="project-group-section__body" ref={ungroupedBodyRef}>
                      {isMobile ? (
                        !showSkeleton && flatItems.length === 0 ? (
                          emptyUngroupedTemplate()
                        ) : (
                          renderCards(flatItems, kpis, kpisLoading, showSkeleton)
                        )
                      ) : (
                        <SimpleBar style={{ maxHeight: '100vh' }} autoHide={false}>
                          <DataTable
                            lazy
                            stripedRows
                            scrollable
                            sortField={sortField}
                            sortOrder={dtSortOrder}
                            onSort={(e) => {
                              if (e.sortField) {
                                setSortField(String(e.sortField));
                                setSortOrder(e.sortOrder === 1 ? 'ASC' : 'DESC');
                                setPage(0);
                                setFirst(0);
                              }
                            }}
                            value={showSkeleton ? makeSkeletonRows<ProjectResponse>(pageSize) : flatItems}
                            tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                            tableClassName={`table${
                              draggedProjectId != null ? ' table--draggable-rows' : ''
                            }`}
                            className="table-wrapper table-wrapper--accordion"
                            emptyMessage={emptyUngroupedTemplate}
                            dataKey="id"
                            rowClassName={rowClassName}
                          >
                            {buildColumns(flatItems, kpis, kpisLoading)}
                          </DataTable>
                        </SimpleBar>
                      )}
                      <Paginator
                        className="pagination"
                        first={first}
                        rows={pageSize}
                        totalRecords={total}
                        rowsPerPageOptions={[10, 25, 50]}
                        currentPageReportTemplate={t('rowsNumber') + ':'}
                        template={
                          isMobile
                            ? MOBILE_PAGINATOR_TEMPLATE
                            : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                        }
                        onPageChange={handleFlatPaginatorChange}
                        pageLinkSize={isMobile ? 3 : 5}
                      />
                      <ModelTooltips key={pageProjectIds.join('-')} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // ── Flat mode: server-paginated SimpleBar table ───────────────────
          <div className="table-content table-content--simplebar">
            <div className="accordion-table-frame">
              {isMobile ? (
                !showSkeleton && flatItems.length === 0 ? (
                  emptyFlatTemplate()
                ) : (
                  renderCards(flatItems, kpis, kpisLoading, showSkeleton)
                )
              ) : (
                <SimpleBar style={{ maxHeight: '100vh' }} autoHide={false}>
                  <DataTable
                    lazy
                    stripedRows
                    scrollable
                    sortField={sortField}
                    sortOrder={dtSortOrder}
                    onSort={(e) => {
                      if (e.sortField) {
                        setSortField(String(e.sortField));
                        setSortOrder(e.sortOrder === 1 ? 'ASC' : 'DESC');
                        setPage(0);
                        setFirst(0);
                      }
                    }}
                    value={showSkeleton ? makeSkeletonRows<ProjectResponse>(pageSize) : flatItems}
                    tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                    tableClassName="table"
                    className="table-wrapper table-wrapper--accordion"
                    emptyMessage={emptyFlatTemplate}
                    dataKey="id"
                    rowClassName={rowClassName}
                  >
                    {buildColumns(flatItems, kpis, kpisLoading)}
                  </DataTable>
                </SimpleBar>
              )}
              <Paginator
                className="pagination"
                first={first}
                rows={pageSize}
                totalRecords={total}
                rowsPerPageOptions={[10, 25, 50]}
                currentPageReportTemplate={t('rowsNumber') + ':'}
                template={
                  isMobile
                    ? MOBILE_PAGINATOR_TEMPLATE
                    : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                }
                onPageChange={handleFlatPaginatorChange}
                pageLinkSize={isMobile ? 3 : 5}
              />
            </div>
            {/* key tied to the rendered rows: PrimeReact Tooltip binds to `.ico--*` once (no DOM
                observer), and the skeleton means the icons don't exist on first bind — remounting on
                each row-set change re-binds it to the current icons. */}
            <ModelTooltips key={pageProjectIds.join('-')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
